import React, { useState, useEffect } from 'react';
import { companyDocumentService } from '../services/api';

const CompanyDocumentsManager = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [newUrl, setNewUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await companyDocumentService.listDocuments();
      // Handle the response format from server
      const docs = response.data?.documents || response.data || [];
      setDocuments(docs);
    } catch (error) {
      console.error('Error loading documents:', error);
      setMessage({ type: 'error', text: 'Failed to load documents' });
    } finally {
      setLoading(false);
    }
  };

  const addDocument = async () => {
    if (!newUrl.trim()) {
      setMessage({ type: 'error', text: 'Please enter a valid Confluence URL' });
      return;
    }

    // Basic URL validation
    if (!newUrl.includes('atlassian.net') && !newUrl.includes('confluence')) {
      setMessage({ type: 'error', text: 'Please enter a valid Confluence URL' });
      return;
    }

    try {
      setAdding(true);
      const response = await companyDocumentService.ingestDocument(newUrl);
      
      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: `Document "${response.data.title}" ingested successfully (${response.data.chunksProcessed} chunks)` 
        });
        setNewUrl('');
        await loadDocuments();
      } else {
        setMessage({ type: 'error', text: 'Failed to ingest document' });
      }
    } catch (error) {
      console.error('Error adding document:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to add document' 
      });
    } finally {
      setAdding(false);
    }
  };

  const resyncDocument = async (sourceUrl, documentTitle) => {
    try {
      setSyncing(sourceUrl);
      const response = await companyDocumentService.ingestDocument(sourceUrl);
      
      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: `Document "${documentTitle}" re-synced successfully (${response.data.chunksProcessed} chunks)` 
        });
        await loadDocuments();
      } else {
        setMessage({ type: 'error', text: 'Failed to re-sync document' });
      }
    } catch (error) {
      console.error('Error re-syncing document:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to re-sync document' 
      });
    } finally {
      setSyncing(null);
    }
  };

  const removeDocument = async (sourceUrl, documentTitle) => {
    if (!window.confirm(`Are you sure you want to remove "${documentTitle}"? This will delete all indexed chunks.`)) {
      return;
    }

    try {
      await companyDocumentService.deleteDocument(sourceUrl);
      setMessage({ type: 'success', text: `Document "${documentTitle}" removed successfully` });
      await loadDocuments();
    } catch (error) {
      console.error('Error removing document:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to remove document' 
      });
    }
  };

  const clearMessage = () => {
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  useEffect(() => {
    if (message.text) clearMessage();
  }, [message]);

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2" style={{color:'rgba(255,255,255,0.85)'}}>Company Guidelines from Confluence</h3>
        <p className="text-sm mb-6" style={{color:'rgba(255,255,255,0.6)'}}>
          Add Confluence pages containing company guidelines, best practices, and standards. 
          These documents will be used to enhance AI responses with company-specific knowledge.
        </p>

        {/* Message Display */}
        {message.text && (
          <div className="mb-4 p-3 rounded-lg" style={message.type==='success'?{background:'rgba(130,255,207,0.12)',border:'1px solid rgba(130,255,207,0.3)',color:'rgba(130,255,207,0.9)'}:{background:'rgba(230,100,97,0.12)',border:'1px solid rgba(230,100,97,0.3)',color:'rgba(230,100,97,0.9)'}}>
            {message.text}
          </div>
        )}

        {/* Add New Document */}
        <div className="rounded-lg p-3 mb-4" style={{background:'rgba(130,255,207,0.05)',border:'1px solid var(--tqa-border-dark)'}}>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addDocument()}
              placeholder="Enter Confluence URL..."
              className="flex-1 px-3 py-1.5 text-sm rounded-md shadow-sm focus:outline-none" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
              disabled={adding}
            />
            <button
              onClick={addDocument}
              disabled={adding || !newUrl.trim()}
              className="btn-primary px-4 py-1.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 whitespace-nowrap"
            >
              {adding ? (
                <>
                  <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Adding...
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Documents List */}
      <div>
        <h4 className="text-sm font-semibold mb-3" style={{color:'rgba(255,255,255,0.65)'}}>
          Indexed Documents ({documents.length})
        </h4>

        {loading ? (
          <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{borderColor:'var(--tqa-green)'}}></div>
        </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12 rounded-lg" style={{background:'rgba(255,255,255,0.03)',border:'2px dashed var(--tqa-border-dark)'}}>
            <svg className="w-12 h-12 mx-auto mb-3" style={{color:'rgba(255,255,255,0.3)'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="font-medium" style={{color:'rgba(255,255,255,0.55)'}}>No documents indexed yet</p>
            <p className="text-sm mt-1" style={{color:'rgba(255,255,255,0.4)'}}>Add your first Confluence page to get started</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {documents.map((doc, index) => (
              <div
                key={index}
                className="rounded-lg p-4 transition-shadow" style={{background:'var(--tqa-surface)',border:'1px solid var(--tqa-border-dark)'}}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-5 h-5 flex-shrink-0" style={{color:'var(--tqa-green)'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <h5 className="text-base font-semibold truncate" style={{color:'rgba(255,255,255,0.85)'}}>
                        {doc.documentTitle || 'Untitled Document'}
                      </h5>
                    </div>
                    
                    <a
                      href={doc.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs hover:underline flex items-center gap-1 truncate"
                      style={{color: 'var(--tqa-blue)'}}
                    >
                      <span className="truncate">{doc.sourceUrl}</span>
                      <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>

                  <div className="flex flex-col items-end gap-2 ml-4">
                    <div className="flex items-center gap-2">
                    <button
                      onClick={() => resyncDocument(doc.sourceUrl, doc.documentTitle)}
                      disabled={syncing === doc.sourceUrl}
                      style={{color:'rgba(130,255,207,0.8)',border:'1px solid rgba(130,255,207,0.25)',borderRadius:'6px'}}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(130,255,207,0.08)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                      className="px-3 py-1.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                      title="Re-sync document"
                    >
                      {syncing === doc.sourceUrl ? (
                        <>
                          <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Syncing...
                        </>
                      ) : (
                        <>
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Resync
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => removeDocument(doc.sourceUrl, doc.documentTitle)}
                      disabled={syncing === doc.sourceUrl}
                      style={{color:'rgba(230,100,97,0.8)',border:'1px solid rgba(230,100,97,0.25)',borderRadius:'6px'}}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(230,100,97,0.08)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                      className="px-3 py-1.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                      title="Remove document"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Remove
                    </button>
                    </div>
                    {doc.lastSyncedAt && (
                      <div className="text-xs flex items-center gap-1" style={{color:'rgba(255,255,255,0.4)'}}>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Last synced: {new Date(doc.lastSyncedAt).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyDocumentsManager;
